import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import updateSimulationAgentPersona from 'redux/actions/Common/updateSimulationAgentPersona';

/**
 * Inline persona editor for one agent (PATCH …/persona). No confirmation step —
 * an edit is instantly reversible by saving again, and takes effect on the
 * agent's next tick without pausing.
 *
 * No endpoint returns an agent's *current* persona, so `persona` is either the
 * text saved from this page in this session (`source: 'saved'`) or the text
 * entered at creation via GET /setup (`source: 'creation'`), which won't
 * reflect edits made elsewhere or before a reload.
 */
const PersonaEditor = ({
  simulationId,
  agent,
  persona,
  disabled,
  onSaved,
  onError,
}) => {
  const dispatch = useDispatch();
  const agentId = agent?.agentConversationId;
  const initialText = persona?.text || '';

  const [text, setText] = useState(initialText);
  const [saving, setSaving] = useState(false);

  // Reset the draft when switching agents or when the known persona changes.
  useEffect(() => {
    setText(initialText);
  }, [agentId, initialText]);

  const trimmed = text.trim();
  const dirty = trimmed !== initialText.trim();

  const handleSave = () => {
    if (!trimmed || !dirty || saving) return;
    setSaving(true);
    dispatch(
      updateSimulationAgentPersona(
        simulationId,
        agentId,
        trimmed,
        (data) => {
          setSaving(false);
          onSaved(agentId, data?.personaPrompt ?? trimmed);
        },
        (message) => {
          setSaving(false);
          onError(message);
        },
      ),
    );
  };

  const sourceHint =
    persona?.source === 'saved'
      ? `Saved from this page ${moment(persona.savedAt).fromNow()}.`
      : persona?.source === 'creation'
      ? 'Showing the persona entered at creation — edits made since (elsewhere or before a reload) aren’t returned by the API.'
      : 'The current persona isn’t available from the API — saving sets a new one.';

  return (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <Typography variant={'subtitle2'} fontWeight={'bold'}>
            Persona
          </Typography>
          {persona?.source === 'saved' ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label="edited"
            />
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {disabled ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            This simulation has finished — personas can no longer be changed.
          </Alert>
        ) : null}
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={12}
          size="small"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disabled || saving}
          placeholder="Describe who this agent is and how they behave in the deal"
          helperText={sourceHint}
        />
        <Box display={'flex'} justifyContent={'flex-end'} gap={1} mt={1}>
          <Button
            size="small"
            onClick={() => setText(initialText)}
            disabled={disabled || saving || !dirty}
          >
            Reset
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={disabled || saving || !dirty || !trimmed}
          >
            {saving ? (
              <>
                Saving...
                <CircularProgress size={14} sx={{ ml: 1 }} />
              </>
            ) : initialText ? (
              'Save persona'
            ) : (
              'Set persona'
            )}
          </Button>
        </Box>
        <Typography
          variant={'caption'}
          color={'text.secondary'}
          display={'block'}
          mt={0.5}
        >
          Takes effect on this agent’s next tick — use “Force tick” to see it
          right away.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export default PersonaEditor;
